import { Terminal, CheckCircle, XCircle } from "lucide-react";
import { useState } from "react";

import { confirmCliAuth } from "@/lib/api";

type Status = "idle" | "confirming" | "success" | "error";

export function CliAuthView() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  // Read user code from URL query params
  const params = new URLSearchParams(window.location.search);
  const userCode = params.get("code");

  const handleConfirm = async () => {
    if (!userCode) return;
    setStatus("confirming");
    setErrorMessage("");

    try {
      await confirmCliAuth(userCode);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Failed to authorize");
    }
  };

  if (!userCode) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <div className="text-center">
          <XCircle size={48} className="mx-auto mb-4 text-cyber-red" />
          <h1 className="mb-2 text-lg font-bold text-cyber-text">Invalid Link</h1>
          <p className="text-sm text-cyber-muted">
            No authorization code found. Run{" "}
            <code className="text-cyber-cyan">bun run sync:env</code> to start.
          </p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <div className="text-center">
          <CheckCircle size={48} className="mx-auto mb-4 text-cyber-green" />
          <h1 className="mb-2 text-lg font-bold text-cyber-text">CLI Authorized</h1>
          <p className="text-sm text-cyber-muted">
            You can close this tab and return to your terminal.
          </p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <div className="text-center">
          <XCircle size={48} className="mx-auto mb-4 text-cyber-red" />
          <h1 className="mb-2 text-lg font-bold text-cyber-text">Authorization Failed</h1>
          <p className="mb-4 text-sm text-cyber-muted">{errorMessage}</p>
          <p className="text-sm text-cyber-muted">
            Run <code className="text-cyber-cyan">bun run sync:env</code> again to get a new code.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center">
      <div className="w-full max-w-sm text-center">
        <Terminal size={48} className="mx-auto mb-4 text-cyber-cyan" />
        <h1 className="mb-2 text-lg font-bold text-cyber-text">Authorize CLI Access</h1>
        <p className="mb-6 text-sm text-cyber-muted">Verify this code matches your terminal:</p>

        {/* User code display */}
        <div className="mb-6 rounded-lg border border-cyber-cyan/30 bg-cyber-cyan/5 px-6 py-4">
          <span className="font-mono text-3xl font-bold tracking-widest text-cyber-cyan">
            {userCode}
          </span>
        </div>

        <button
          type="button"
          onClick={handleConfirm}
          disabled={status === "confirming"}
          className="w-full rounded border border-cyber-cyan/30 bg-cyber-cyan/10 px-6 py-3 text-sm font-medium text-cyber-cyan hover:bg-cyber-cyan/20 disabled:opacity-50"
        >
          {status === "confirming" ? "Authorizing..." : "Authorize"}
        </button>
      </div>
    </div>
  );
}
